import type { UpcomingVisit } from '@/api/types/ProviderApi'

/**
 * What happens at a visit, in the order it happens.
 *
 * ## Why this lives in the app
 *
 * `AppointmentType` is an enum in `kapi-core`, not a table — twenty values with a name, an id, a
 * colour and a default duration, and nowhere to hang a paragraph. The server-side home for this
 * is one `expectation_groups` record carrying `steps`, a film and a poster, with a type → group
 * map beside the enum; the app would then read it off the visit. Until the copy stops moving,
 * that record is this file: the same shape, seeded here, so the words can be argued over in a
 * pull request instead of a migration.
 *
 * ## Groups, not types
 *
 * Twenty step-lists is not a thing anyone will write or keep true, and a member cannot tell an
 * Aligner Mid-Course IPR from an Aligner Fitting Adjustment by what the half hour feels like.
 * Five groups can: an evaluation, a cleaning, a scan, a fitting, a treatment. Every type names
 * one.
 *
 * ## The copy is clinical and is not signed off
 *
 * These sentences tell a member what will be done to them. They are written from what the studio
 * does today and they are deliberately short of specifics for that reason — no instrument names,
 * no counts, nothing about outcomes. **The clinical team owns this text**, and the day it is
 * reviewed is the day it should move to the server.
 */

/** One step of the visit: what it is, and the one thing worth knowing about it. */
export type ExpectationStep = { title: string; line: string }

/**
 * The film, where there is one.
 *
 * `source` is what a player is handed and `poster` is the still the card shows — the two are not
 * the same picture, and the poster is the one that has to survive being 132 points tall.
 */
export type ExpectationVideo = {
	title: string
	/** How long it runs, as a member reads a clock: "0:48". */
	length: string
	source: number | string
	poster: number
}

export type ExpectationGroup = {
	steps: ExpectationStep[]
	video?: ExpectationVideo
}

export type ExpectationGroupKey =
	| 'evaluation'
	| 'cleaning'
	| 'scan'
	| 'fitting'
	| 'treatment'

/**
 * The only film in the repository: three seconds of brand animation, shipped for the splash.
 *
 * It stands in so the card can be seen, tapped and played on a device rather than only in a
 * mock — and it is labelled as what it is rather than as a tour it is not. The per-group films
 * replace it one field at a time.
 *
 * The poster is the brand book's own tools-and-tech photograph, which is the studio a member is
 * about to sit in. It is not a frame of the film; a poster never has to be.
 */
const STAND_IN_FILM: ExpectationVideo = {
	title: 'A look inside Wally',
	length: '0:03',
	source: require('#/assets/video/splash-intro.mp4'),
	poster: require('#/assets/images/chair.jpg'),
}

/**
 * Check-in leads every group, in the same words.
 *
 * It is the one step that is true of all twenty types, and the only one that says something a
 * member can act on: their forms are already done, so there is no clipboard at the desk.
 */
const CHECK_IN: ExpectationStep = {
	title: 'Check-in',
	line: 'A quick hello at the desk — your forms are already done in the app.',
}

export const GROUPS: Record<ExpectationGroupKey, ExpectationGroup> = {
	evaluation: {
		video: STAND_IN_FILM,
		steps: [
			CHECK_IN,
			{
				title: 'Photos, scans and x-rays',
				line: 'A handheld scanner and low-radiation images. Nothing sharp and nothing cold.',
			},
			{
				title: 'Your clinician takes a look',
				line: 'Gums, teeth and bite, one at a time, with everything they have just captured on the screen beside you.',
			},
			{
				title: 'Your Wally Score',
				line: 'What we found and what it means, before you leave — and in the app afterwards.',
			},
		],
	},
	cleaning: {
		steps: [
			CHECK_IN,
			{
				title: 'A look at what has changed',
				line: 'Your clinician checks your gums and teeth against your last visit.',
			},
			{
				title: 'Airflow cleaning',
				line: 'Warm water, air and a fine powder instead of metal scraping.',
			},
			{
				title: 'What we found',
				line: 'Anything worth watching, said out loud rather than left in a chart.',
			},
		],
	},
	scan: {
		steps: [
			CHECK_IN,
			{
				title: 'The scan',
				line: 'A handheld wand builds a model of your mouth. A few minutes, and no impression trays.',
			},
			{
				title: 'What happens next',
				line: 'Your scan goes to your clinician, and we message you when there is something to see.',
			},
		],
	},
	fitting: {
		steps: [
			CHECK_IN,
			{
				title: 'The fitting',
				line: 'We fit it, adjust it, and check it feels right before you leave.',
			},
			{
				title: 'Living with it',
				line: 'How to put it in, take it out and keep it clean — and what to do if it stops feeling right.',
			},
		],
	},
	treatment: {
		steps: [
			CHECK_IN,
			{
				title: 'The treatment',
				line: 'We clean and dry the area, then apply it. A few minutes, and nothing that needs numbing.',
			},
			{
				title: 'Afterwards',
				line: 'Eat and drink as you normally would. We check on it at your next visit.',
			},
		],
	},
}

/**
 * Every appointment type, and the group it belongs to.
 *
 * Keyed by the enum's own name — `AppointmentTypeRef.value`, which is what the payload carries —
 * so a type added to `kapi-core` shows up here as a missing key rather than as wrong copy.
 * `VIP` is in the list because the enum has it; it is not patient-bookable, but a studio can put
 * one on a member's calendar and the page has to say something.
 */
const GROUP_OF: Record<string, ExpectationGroupKey> = {
	NEW_PATIENT: 'evaluation',
	RENEWAL: 'evaluation',
	PERIO_EVAL: 'evaluation',
	VIP: 'evaluation',
	RECARE: 'cleaning',
	PERIO_MAINT: 'cleaning',
	PERIO_THERAPY: 'cleaning',
	PERIO_RENEWAL: 'cleaning',
	SCAN: 'scan',
	ALIGNER_REFINEMENT_RECORDS: 'scan',
	ALIGNER_INITIAL_FITTING: 'fitting',
	ALIGNER_MIDCOURSE_IPR: 'fitting',
	ALIGNER_REFINEMENT_FITTING: 'fitting',
	ALIGNER_FITTING_ADJUSTMENT: 'fitting',
	ALIGNER_COMPLETION: 'fitting',
	NIGHTGUARD_ADJUSTMENT: 'fitting',
	ATTACHMENT_REMOVAL: 'fitting',
	FIXED_RETAINER_REMOVAL: 'fitting',
	FIXED_RETAINER_REMOVAL_DELIVERY: 'fitting',
	CURODONT: 'treatment',
}

/**
 * Which group wins when a visit has several types.
 *
 * `appointment_details_log.types` is a JSON array and a studio books a Recare with a Scan on one
 * slot often enough that this is the normal case, not the edge. Ranked by what the half hour is
 * mostly about rather than by the enum's durations: a Recare with a Scan attached is a cleaning
 * with a scan in it, and a New Patient with anything else attached is still a first visit.
 */
const PRECEDENCE: ExpectationGroupKey[] = [
	'evaluation',
	'cleaning',
	'treatment',
	'fitting',
	'scan',
]

/**
 * What to expect at this visit, or nothing.
 *
 * Nothing where the visit's types are all unknown to the map — a type added to the enum before
 * its copy was written. The section draws nothing rather than guessing, because a wrong list of
 * what is about to happen to someone is worse than no list.
 */
export const expectationsFor = (visit: UpcomingVisit): ExpectationGroup | null => {
	const keys = visit.types
		.map((type) => GROUP_OF[type.value])
		.filter((key): key is ExpectationGroupKey => !!key)

	const winner = PRECEDENCE.find((key) => keys.includes(key))
	return winner ? GROUPS[winner] : null
}

/**
 * "Perio Evaluation · about 30 min" — what the visit is, and roughly how long it takes.
 *
 * "About", where the card's own type line says "30 min" flat: the card is quoting the slot the
 * studio booked and this is telling a member how much of their afternoon to set aside.
 *
 * Every type, not the first one: this line is the only place a Recare booked with a Scan admits
 * that the Scan is happening.
 */
export const expectationsSubtitle = (visit: UpcomingVisit) => {
	const names = visit.types.map((type) => type.name).filter(Boolean)
	const what = names.length > 0 ? names.join(' & ') : 'Your visit'
	return visit.durationInMinutes ? `${what} · about ${visit.durationInMinutes} min` : what
}
